import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BD } from "@/constants/testIds";
import BridgeDatumLogo from "@/components/shared/BridgeDatumLogo";

const links = [
  { to: "/", labelKey: "nav.home", end: true },
  { to: "/about", labelKey: "nav.about" },
  { to: "/solutions", labelKey: "nav.solutions" },
  { to: "/advisory", labelKey: "nav.advisory" },
  { to: "/intelligence", labelKey: "nav.intelligence" },
  { to: "/sustainability", labelKey: "nav.sustainability" },
  { to: "/sectors", labelKey: "nav.sectors" },
  { to: "/insights", labelKey: "nav.insights" },
  { to: "/contact", labelKey: "nav.contact" },
];

const BDNavbar = () => {
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={BD.nav}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white backdrop-blur-xl border-b border-[#0A192F]/10 shadow-sm" : "bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-28 sm:h-32 flex items-center justify-between">
        <Link to="/" data-testid={BD.navLogo} className="flex items-center gap-3">
          <BridgeDatumLogo variant="navbar" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-medium transition-colors ${
                  isActive ? "text-[#0A192F]" : "text-[#0A192F]/60 hover:text-[#0A192F]"
                }`
              }
            >
              {t(l.labelKey)}
            </NavLink>
          ))}
          <Link
            to="/eisg"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#F58220] hover:text-[#D4AF37] transition-colors"
            data-testid="bd-nav-eisg-link"
          >
            EISG <ArrowUpRight size={14} />
          </Link>
          <button
            onClick={toggle}
            data-testid={BD.langSwitch}
            className="ml-2 text-[11px] font-semibold tracking-[0.25em] border border-[#0A192F]/30 px-3 py-1.5 hover:border-[#0A192F] hover:bg-[#0A192F] hover:text-white transition-all"
          >
            {lang === "en" ? "EN · FR" : "FR · EN"}
          </button>
        </nav>

        <button
          data-testid={BD.navMobileToggle}
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 text-[#0A192F]"
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#0A192F]/10 bg-white">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? "text-[#0A192F]" : "text-[#0A192F]/70"}`
                }
              >
                {t(l.labelKey)}
              </NavLink>
            ))}
            <Link to="/eisg" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#F58220]">
              EISG →
            </Link>
            <button
              onClick={toggle}
              className="self-start text-[11px] font-semibold tracking-[0.25em] border border-[#0A192F]/30 px-3 py-1.5"
            >
              {lang === "en" ? "EN · FR" : "FR · EN"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default BDNavbar;
